import { kebabCase, startCase, upperFirst } from 'es-toolkit';

import { DEFAULT_FILTER_VALUE } from '../constants/PremiumConstants';
import PropertyConstants from '../constants/PropertyConstants';

import numberUtils from './numberUtils';
import stringHelper from './stringHelper';

import type { CurrentPropertyProps } from '../model/CurrentProperty';
import type { FloorPlanProps } from '../model/FloorPlan';

const { PROPERTY_TYPES_BY_CODE } = PropertyConstants;

const upperFirstEveryWord = (string: string) => string.split(' ').map(upperFirst).join(' ');

const propertyUtils = {
  checkIsAmenityFieldFulfilled: ({
    amenityField,
  }: {
    amenityField?: CurrentPropertyProps['amenities']['interior'] | CurrentPropertyProps['amenities']['property'];
  }) => {
    const requiredArray = amenityField?.filter(({ isRequired }) => isRequired);

    if (!requiredArray?.length) {
      return true;
    }

    return Boolean(
      requiredArray?.some(({ values }) => {
        let fulfilled = false;

        values.forEach(({ isSelected }) => {
          fulfilled = fulfilled || isSelected;
        });

        return fulfilled;
      }),
    );
  },
  formatAddress(addressComponents: Partial<Record<'street' | 'city' | 'state' | 'unit' | 'name' | 'zip', string>>) {
    const { street, city, state, unit, name, zip } = addressComponents;

    // Transformations
    let formattedStreet = street ? upperFirstEveryWord(street) : '';
    const formattedCity = city ? startCase(city) : '';
    const formattedState = state ? state.toUpperCase() : '';
    const formattedUnit = unit ? `#${unit.toUpperCase()}` : '';
    const formattedName = name ? upperFirstEveryWord(name) : '';

    const formattedAddressWithoutUnit = [formattedStreet, formattedCity, `${formattedState} ${zip}`]
      .filter((val) => val)
      .join(', ');

    if (unit && unit.length) {
      formattedStreet += ` ${formattedUnit}`;
    }

    const formattedAddress = [formattedStreet, formattedCity, formattedState].filter((val) => val).join(', ');
    const formattedCityState = [formattedCity, formattedState].filter((val) => val).join(', ');
    const formattedCityStateZip = `${formattedCityState} ${zip}`;
    const formattedCompleteAddress = [`${formattedStreet},`, formattedCityState, zip].filter((val) => val).join(' ');

    // Remove ordinal suffixes, apply kebabCase, then add them back
    const withoutOrdinals = formattedAddressWithoutUnit.replace(/(\d+)(st|nd|rd|th)/gi, '$1');
    const formattedKebobCase = kebabCase(withoutOrdinals).replace(/(\d+)(?=-|$)/g, (match) => {
      const original = formattedAddressWithoutUnit.match(new RegExp(`${match}(st|nd|rd|th)`, 'i'));
      return original ? original[0].toLowerCase() : match;
    });

    return {
      formattedAddressWithoutUnit,
      formattedAddress,
      formattedCityState,
      formattedCityStateZip,
      formattedCompleteAddress,
      formattedName,
      formattedStreet,
      formattedKebobCase,
    };
  },
  formatFloorPlanDisplayPrice(lowPrice: number, highPrice: number) {
    const formattedLowPrice = numberUtils.asCurrencyWithDefault(lowPrice, '', '$0,0');
    const formattedHighPrice = numberUtils.asCurrencyWithDefault(highPrice, '', '$0,0');

    switch (true) {
      case !lowPrice && !highPrice:
        return '$ -';
      case lowPrice === highPrice:
        return `${formattedLowPrice}`;
      case Boolean(lowPrice && highPrice):
        return `${formattedLowPrice}-${formattedHighPrice}`;
      default:
        return `${formattedLowPrice || formattedHighPrice}`;
    }
  },
  getActiveFloorPlanOrUnitCount(floorPlans: Array<FloorPlanProps>) {
    let activeCount = 0;

    floorPlans.forEach((floorPlan) => {
      floorPlan.units &&
        floorPlan.units.forEach((unit) => {
          if (unit.active) {
            activeCount += 1;
          }
        });

      if (floorPlan?.units?.length === 0 && floorPlan.active) {
        activeCount += 1;
      }
    });

    return activeCount;
  },
  getFloorPlanDescription({ baths, beds, lowPrice, highPrice, squareFeet, units }: FloorPlanProps) {
    const activeUnits = units
      ? units.reduce((accumulator: Array<number>, unit) => {
          if (unit.active) {
            accumulator.push(Number(unit.price));
          }
          return accumulator;
        }, [])
      : [];

    const numActive = activeUnits.length;
    const lowActivePrice = numActive && Math.min(...activeUnits);
    const highActivePrice = numActive && Math.max(...activeUnits);

    const displayPrice = this.formatFloorPlanDisplayPrice(
      lowActivePrice || Number(lowPrice),
      highActivePrice || Number(highPrice),
    );
    const displayTextForBaths = !baths ? '- baths' : `${stringHelper.pluralize(baths, 'bath', 'baths')}`;
    const displaySqft = squareFeet ? ` | ${squareFeet} sqft` : '';
    const displayBeds = beds || beds === 0 ? Number(beds) : '-';
    const displayTextForBeds = displayBeds === 0 ? 'studio' : `${stringHelper.pluralize(displayBeds, 'bed', 'beds')}`;

    return {
      displayPrice,
      fullDisplayText: `${displayPrice} | ${displayTextForBeds} | ${displayTextForBaths}${displaySqft}`,
    };
  },
  getListingId(feedId: string, feedListingId: string) {
    return `${feedListingId}_${feedId}`;
  },
  getIsPetsAllowedEmpty(petsAllowed: CurrentPropertyProps['petsAllowed']) {
    return !Object.values(petsAllowed).some((petsAllowedValue) =>
      typeof petsAllowedValue === 'boolean' ? petsAllowedValue !== false : petsAllowedValue?.allowed !== null,
    );
  },
  isSingleFamilyRental({ currentProperty }: { currentProperty: { isMultifamily: boolean; listingTypeCode: number } }) {
    // todo
    const { isMultifamily, listingTypeCode } = currentProperty;

    const response =
      !isMultifamily &&
      // type code 1 = Rental
      listingTypeCode === 1;

    return response;
  },
  isRoomForRent({ currentProperty }: { currentProperty: Partial<CurrentPropertyProps> }) {
    const { listingTypeCode } = currentProperty;

    return listingTypeCode === PropertyConstants.LISTING_CODES_BY_TYPE[PropertyConstants.LISTING_TYPES.ROOM];
  },
  isPremiumListing({ currentProperty }: { currentProperty: CurrentPropertyProps }) {
    return (
      currentProperty?.freemium?.isEnabled &&
      currentProperty?.freemium?.activeProduct?.type === PropertyConstants.PRODUCT_TYPE_NAME_TO_ID.PREMIUM
    );
  },
  isPreviouslyActive({ currentProperty }: { currentProperty: CurrentPropertyProps }) {
    return !currentProperty?.active && currentProperty?.dateLastActivatedTimestamp;
  },
  formatSearchString(
    beds: string | undefined,
    propertyType: number | string | undefined,
    searchArea: string,
    capitalize = false,
  ) {
    let searchString;
    switch (true) {
      case beds === DEFAULT_FILTER_VALUE && propertyType === DEFAULT_FILTER_VALUE:
        searchString = `all rentals in ${searchArea}`;
        break;
      case propertyType === DEFAULT_FILTER_VALUE:
        searchString = `${beds} bedroom rentals in ${searchArea}`;
        break;
      case beds === DEFAULT_FILTER_VALUE:
        searchString = `${PROPERTY_TYPES_BY_CODE[
          propertyType as keyof typeof PROPERTY_TYPES_BY_CODE
        ].toLowerCase()}s in ${searchArea}`;
        break;
      default:
        searchString = `${beds} bedroom ${PROPERTY_TYPES_BY_CODE[
          propertyType as keyof typeof PROPERTY_TYPES_BY_CODE
        ].toLowerCase()}s in ${searchArea}`;
    }
    if (capitalize) {
      return stringHelper.capitalize(searchString);
    }
    return searchString;
  },
};

export default propertyUtils;
